/**
 * How translatable texts are stored and written (plan §8.10, owner i18n-metadata).
 *
 * Every table with translatable texts keeps its text columns (`label`, `name`, `description`, …):
 * they hold the plain text, or the English (else first) value of a localized one, so SQL readers,
 * search, sorting and exports keep working. An `i18n jsonb` column holds the localized values by
 * property, as written: `{ "name": { "en": "All deals", "de": "Alle Verkaufschancen" } }`. A
 * property without an entry is a plain text that reads the same in every language.
 *
 * Writes follow one rule (`nextText`): a localized map replaces the text; a plain string equal to
 * what the writer reads changes nothing; any other plain string replaces a plain text or — when
 * the text is localized — sets only the writer's language. Renaming "Verkaufschancen" on a German
 * screen never overwrites "Deals".
 *
 * Used by the metadata service (objects, fields, options, stages …) and by every owner of a named
 * workspace element: saved views, record layouts, forms, dashboards, workflows and task types.
 */
import { z } from 'zod';
import {
  LOCALIZED_TEXT_LIMITS,
  LocaleCode,
  type LocalizedText,
  type LocalizedTextMap,
  isLocalizedTextMap,
  languageOf,
  resolveText,
} from './localized-text';

/**
 * Schema of a translatable name (views, layouts, forms, dashboards, workflows, task types): a
 * trimmed plain string, or one trimmed value per locale (`{ en: 'All deals', de: 'Alle
 * Verkaufschancen' }`). Values are 1–`maxLength` characters.
 */
export function localizedName(maxLength: number) {
  const value = z.string().trim().min(1).max(maxLength);
  return z.union([value, localeMap(value)]);
}

/** Schema of a translatable description: plain (may be empty) or one value per locale. */
export function localizedDescription(maxLength: number) {
  return z.union([z.string().max(maxLength), localeMap(z.string().min(1).max(maxLength))]);
}

function localeMap(value: z.ZodType<string>) {
  return z
    .record(LocaleCode, value)
    .refine((m) => Object.keys(m).length >= 1, { message: 'Add at least one locale' })
    .refine((m) => Object.keys(m).length <= LOCALIZED_TEXT_LIMITS.maxLocales, {
      message: `At most ${LOCALIZED_TEXT_LIMITS.maxLocales} locales`,
    });
}

/** Localized values of a row as stored in its `i18n` column: property → locale → text. */
export type LabelOverlay = Record<string, LocalizedTextMap>;

/** The sanitised overlay of a stored `i18n` value (`{}` for rows without localized values). */
export function overlayOf(raw: unknown): LabelOverlay {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: LabelOverlay = {};
  for (const [prop, map] of Object.entries(raw as Record<string, unknown>)) {
    if (!map || typeof map !== 'object' || Array.isArray(map)) continue;
    const clean = Object.fromEntries(
      Object.entries(map as Record<string, unknown>).filter(
        (e): e is [string, string] => typeof e[1] === 'string' && e[1].length > 0,
      ),
    );
    if (Object.keys(clean).length > 0) out[prop] = clean;
  }
  return out;
}

/** The text as written: the overlay's localized values, else the plain column text. */
export function textAsWritten(base: string, overlay: LabelOverlay, prop: string): LocalizedText;
export function textAsWritten(base: string | null, overlay: LabelOverlay, prop: string): LocalizedText | null;
export function textAsWritten(
  base: string | null,
  overlay: LabelOverlay,
  prop: string,
): LocalizedText | null {
  return overlay[prop] ?? base;
}

/** The text of a stored property for a reader's language (`resolveText` of what is written). */
export function storedTextFor(
  base: string | null,
  overlay: LabelOverlay,
  prop: string,
  locale: string,
): string {
  const map = overlay[prop];
  return map ? resolveText(map, locale) : (base ?? '');
}

/** Column text and overlay entry of a written value. */
export function storedText(value: LocalizedText): { base: string; map: LocalizedTextMap | null } {
  if (!isLocalizedTextMap(value)) return { base: value, map: null };
  const map = Object.fromEntries(Object.entries(value).filter(([, v]) => v.length > 0));
  if (Object.keys(map).length === 0) return { base: '', map: null };
  return { base: resolveText(map, 'en'), map };
}

/** Plain column text of a written value (null stays null). */
export function baseText(value: LocalizedText): string;
export function baseText(value: LocalizedText | null): string | null;
export function baseText(value: LocalizedText | null): string | null {
  return value == null ? null : storedText(value).base;
}

/**
 * The value of a translatable text after a write. A localized map replaces the text. A plain
 * string equal to what the writer reads (the text in their language) changes nothing; any other
 * plain string replaces a plain text or, when the text is localized, sets only the writer's
 * language — renaming a label on a German screen never overwrites the English one.
 */
export function nextText(
  current: LocalizedText | null | undefined,
  input: LocalizedText,
  locale: string,
): LocalizedText {
  if (isLocalizedTextMap(input)) return input;
  if (current == null || !isLocalizedTextMap(current)) return input;
  if (resolveText(current, locale) === input) return current;
  if (input.length === 0) return input;
  const keys = Object.keys(current);
  const slot =
    keys.find((k) => k === locale) ??
    keys.find((k) => languageOf(k) === languageOf(locale)) ??
    languageOf(locale);
  return { ...current, [slot]: input };
}

/** The overlay with `prop` set to the localized values of `value` (removed for a plain value). */
export function withOverlayText(
  overlay: LabelOverlay,
  prop: string,
  value: LocalizedText | null | undefined,
): LabelOverlay {
  const next = { ...overlay };
  const map = value == null ? null : storedText(value).map;
  if (map) next[prop] = map;
  else delete next[prop];
  return next;
}

/**
 * Column values of a row after writing translatable texts: `{ [column]: base, i18n }`. `texts`
 * maps a property to its column and the written input (`undefined` keeps the property as is);
 * plain inputs merge into the writer's language (`nextText`). For a new row pass `current` with
 * empty columns and overlay.
 *
 *   writeTexts({ columns: { name: row.name }, overlay: overlayOf(row.i18n) },
 *     { name: input.name }, ctx.locale)
 *   // → { columns: { name: 'All deals' }, i18n: { name: { en: 'All deals', de: 'Alle …' } } }
 */
export function writeTexts<P extends string>(
  current: { columns: Partial<Record<P, string | null>>; overlay: LabelOverlay },
  texts: Partial<Record<P, LocalizedText | null | undefined>>,
  locale: string,
): { columns: Partial<Record<P, string | null>>; i18n: LabelOverlay; changed: boolean } {
  let overlay = { ...current.overlay };
  const columns: Partial<Record<P, string | null>> = {};
  let changed = false;
  for (const prop of Object.keys(texts) as P[]) {
    const input = texts[prop];
    if (input === undefined) continue;
    const before = textAsWritten(current.columns[prop] ?? null, current.overlay, prop);
    const next = input === null ? null : nextText(before, input, locale);
    const base = next === null ? null : baseText(next);
    if (base !== (current.columns[prop] ?? null)) changed = true;
    if (JSON.stringify(next) !== JSON.stringify(before)) changed = true;
    columns[prop] = base;
    overlay = withOverlayText(overlay, prop, next);
  }
  return { columns, i18n: overlay, changed };
}

/**
 * A text with one locale set (`value`) or removed (`null`) — the translations API's write. A plain
 * text keeps its own language (`source`): translating "Deals" into German gives
 * `{ en: 'Deals', de: 'Verkaufschancen' }`.
 */
export function translateText(
  text: LocalizedText,
  locale: string,
  value: string | null,
  source: string,
): LocalizedText {
  const map: LocalizedTextMap = isLocalizedTextMap(text) ? { ...text } : text ? { [source]: text } : {};
  const slot =
    Object.keys(map).find((k) => k === locale) ??
    Object.keys(map).find((k) => languageOf(k) === languageOf(locale)) ??
    locale;
  if (value && value.length > 0) map[slot] = value;
  else if (Object.keys(map).length > 1) delete map[slot];
  const keys = Object.keys(map);
  if (keys.length === 1 && !isLocalizedTextMap(text)) return map[keys[0]!]!;
  return map;
}

/**
 * The value of a text in a locale: the exact locale or a variant of its language. A plain text
 * counts as written in `source` (the language untagged texts of this element are written in).
 */
export function translationOf(
  text: LocalizedText,
  locale: string,
  source: string,
): { value: string | null; source: string; sourceLocale: string } {
  if (!isLocalizedTextMap(text)) {
    return { value: languageOf(locale) === source ? text : null, source: text, sourceLocale: source };
  }
  const keys = Object.keys(text).filter((k) => (text[k] ?? '').length > 0);
  const own = keys.find((k) => k === locale) ?? keys.find((k) => languageOf(k) === languageOf(locale));
  const from =
    keys.find((k) => languageOf(k) === source && languageOf(k) !== languageOf(locale)) ??
    keys.find((k) => languageOf(k) === 'en' && languageOf(k) !== languageOf(locale)) ??
    keys.find((k) => languageOf(k) !== languageOf(locale)) ??
    own!;
  return { value: own ? text[own]! : null, source: text[from] ?? '', sourceLocale: languageOf(from) };
}
